import { SearchResponse } from "@algolia/client-search"
import Footer from "@components/Footer"
import { Container } from "@components/Layout"
import SEO from "@components/SEO"
import { getPageByCodename } from "@lib/kontent"
import { SearchPage } from "@lib/kontent/models-deprecated/search_page"
import { PageProps } from "@lib/types"
import { getFilters } from "@screens/search/get-filters"
import { initialState, reducer } from "@screens/search/reducer"
import { ResultArea } from "@screens/search/result-area"
import SearchInput from "@screens/search/search-input"
import { Result } from "@screens/search/types"
import { useSearchUrlSync } from "@screens/search/use-search-url-sync"
import algoliasearch from "algoliasearch/lite"
import { NextPage } from "next"
import React, { useEffect, useReducer, useState } from "react"

const client = algoliasearch("O3UZK44FAU", "1f9a6d85b4c5b19ce16a7602fbbb99c6")
const index = client.initIndex("article_combinations_and_articles")

export type SearchStatus = "idle" | "loading"
type Props = PageProps<{ content: SearchPage }>

let timer

const Search: NextPage<Props> = ({ data }) => {
  const [status, setStatus] = useState<SearchStatus>("idle")
  const [hits, setHits] = useState<Result[]>([])
  const [basicSearchResponse, setBasicSearchResponse] =
    useState<SearchResponse | null>(null)
  const [subsequentResponse, setSubsequentResponse] =
    useState<SearchResponse | null>(null)
  const [state, dispatch] = useReducer(reducer, initialState)
  const [value, setValue] = useState(() => state.query)
  const popularProducts = data?.content?.popularProducts.value
    ? data?.content?.popularProducts.value.replace(/\s/g, "").split(",")
    : []

  useSearchUrlSync(state, dispatch)

  // Change the basicSearchReponse only when query changes. This determines the visible productGroup filters
  useEffect(() => {
    if (state.query.length > 0) {
      search("basicSearch", "")
    } else {
      setHits([])
    }
  }, [state.query]) // eslint-disable-line

  // Change the subsequentResponse when other query params change. This determines the visibility of the other filters
  useEffect(() => {
    const filters = `NOT product_group:fixed${getFilters(state)}`
    search("subsequent", filters)
  }, [state.productGroup, state.size, state.style, state.hitsPerPage]) // eslint-disable-line

  useEffect(() => {
    if (value.length === 0) {
      setBasicSearchResponse(null)
    }
    clearTimeout(timer)
    timer = setTimeout(() => {
      dispatch({ type: "set-query", query: value })
    }, 500)
    return () => {
      clearTimeout(timer)
    }
  }, [value])

  useEffect(() => {
    setValue(state.initialValue)
  }, [state.initialValue])

  return (
    <>
      <SEO title="Search" urlKey="search" />

      <Container pt={["4rem", "5rem", "5rem", "6rem"]} minHeight="100vh">
        <SearchInput
          onChange={(e) => handleChangeValue(e.target.value)}
          value={value}
        />
        <ResultArea
          hits={hits}
          status={status}
          basicSearchResponse={basicSearchResponse}
          subsequentResponse={subsequentResponse}
          state={state}
          dispatch={dispatch}
          popularProducts={popularProducts}
          value={value}
        />
      </Container>
      <Footer />
    </>
  )

  function handleChangeValue(value: string) {
    dispatch({ type: "clear" })
    setValue(value)
  }

  async function search(
    variant: "subsequent" | "basicSearch",
    filters: string
  ) {
    setStatus("loading")
    const response = await index.search(state.query, {
      hitsPerPage: state.hitsPerPage,
      facets: ["size", "product_group", "style"],
      filters,
    })
    if (variant === "subsequent") {
      setSubsequentResponse(response)
    } else {
      setBasicSearchResponse(response)
    }
    setHits(response.hits)
    setStatus("idle")
  }
}

Search.getInitialProps = async ({ lang }) => {
  try {
    const content = await getPageByCodename({ codename: "search_page", lang })
    if (!content) {
      return { statusCode: 404 }
    }
    return {
      data: { content: content.item as SearchPage },
    }
  } catch (error) {
    console.error(error)
    return { statusCode: 500 }
  }
}

export default Search
